import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';

import LockUserDialog from './LockUserDialog';
import ResetPasswordDialog from './ResetPasswordDialog';
import UnlockUserDialog from './UnlockUserDialog';

const resetPasswordAction = {
  id: 'reset-password',
  title: 'Reset password',
  description: 'Email a temporary password to customer.',
  buttonText: 'Reset Password',
  icon: <AutorenewIcon sx={{ color: '#fff' }} />,
  iconBg: '#159454',
  cardBorder: '2px solid #B8E3CA',
  cardBg: 'linear-gradient(180deg, #E5F4EC 0%, #FFFFFF 80%)',
  buttonVariant: 'contained',
  buttonStyles: {
    backgroundColor: '#7B1F78',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#651863',
    },
  },
};

const lockAction = {
  id: 'admin-lock',
  title: 'Admin lock',
  description: 'Use only for suspected fraud. A lock reason is required.',
  buttonText: 'Lock User',
  icon: <ShieldOutlinedIcon sx={{ color: '#EA5652' }} />,
  iconBg: '#FFF1F1',
  cardBorder: '1px solid #E4E8ED',
  cardBg: '#fff',
  buttonVariant: 'outlined',
  buttonIcon: <LockOutlinedIcon />,
  buttonStyles: {
    borderColor: '#F3B1AF',
    color: '#EA5652',
    '&:hover': {
      borderColor: '#EA5652',
    },
  },
};

const unlockAction = {
  id: 'admin-unlock',
  title: 'Admin lock',
  description: 'Admin Locked. Remove the Admin Lock to re-enable user access.',
  buttonText: 'Unlock User',
  icon: <LockOpenOutlinedIcon sx={{ color: '#159454' }} />,
  iconBg: '#E6F2EC',
  cardBorder: '1px solid #E4E8ED',
  cardBg: '#fff',
  buttonVariant: 'outlined',
  buttonIcon: <LockOpenOutlinedIcon />,
  buttonStyles: {
    borderColor: '#A8D8BE',
    color: '#159454',
    '&:hover': {
      borderColor: '#159454',
    },
  },
};

function ActionCards({ customer, adminLocked, onAdminLockChange }) {
  const [resetOpen, setResetOpen] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);

  const actions = [resetPasswordAction, adminLocked ? unlockAction : lockAction];

  const handleActionClick = (actionId) => {
    if (actionId === 'reset-password') {
      setResetOpen(true);
    } else if (actionId === 'admin-lock') {
      setLockOpen(true);
    } else if (actionId === 'admin-unlock') {
      setUnlockOpen(true);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: '1px solid #E4E8ED',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            borderBottom: '1px solid #E4E8ED',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ArrowForwardIcon sx={{ color: '#F58220', fontSize: 20 }} />

          <Typography
            sx={{
              fontSize: '14.5px !important',
              fontWeight: 700,
              color: '#102A43',
            }}
          >
            Choose the action
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Cards stack on phones and sit side by side from tablet up. */}
          <Grid container spacing={2}>
            {actions.map((action) => (
              <Grid item xs={12} sm={6} key={action.title}>
                <Card
                  sx={{
                    border: action.cardBorder,
                    borderRadius: '12px',
                    boxShadow: 'none',
                    height: '100%',
                    background: action.cardBg,
                  }}
                >
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '10px',
                          bgcolor: action.iconBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {action.icon}
                      </Box>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: '15px !important',
                        fontWeight: 700,
                        color: '#102A43',
                        mb: 1,
                      }}
                    >
                      {action.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#5B7184',
                        fontSize: '12.5px !important',
                        fontWeight: 400,
                        lineHeight: { xs: '20px', sm: '24px' },
                        mb: { xs: 2, sm: 3 },
                      }}
                    >
                      {action.description}
                    </Typography>

                    <Button
                      fullWidth
                      variant={action.buttonVariant}
                      startIcon={action.buttonIcon || <LockOutlinedIcon />}
                      onClick={() => handleActionClick(action.id)}
                      sx={{
                        borderRadius: '30px',
                        py: 1.2,
                        height: 'auto',
                        minHeight: 40,
                        textTransform: 'none',
                        fontWeight: 700,
                        ...action.buttonStyles,
                      }}
                    >
                      {action.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </CardContent>

      <ResetPasswordDialog
        open={resetOpen}
        customer={customer}
        onClose={() => setResetOpen(false)}
      />

      <LockUserDialog
        open={lockOpen}
        customer={customer}
        onClose={() => setLockOpen(false)}
        onSuccess={() => onAdminLockChange?.(true)}
      />

      <UnlockUserDialog
        open={unlockOpen}
        customer={customer}
        onClose={() => setUnlockOpen(false)}
        onSuccess={() => onAdminLockChange?.(false)}
      />
    </Card>
  );
}

ActionCards.propTypes = {
  customer: PropTypes.shape({
    name: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    registeredfrom: PropTypes.string,
    accountStatus: PropTypes.object,
  }),
  adminLocked: PropTypes.bool,
  onAdminLockChange: PropTypes.func,
};

ActionCards.defaultProps = {
  customer: null,
  adminLocked: false,
  onAdminLockChange: null,
};

export default ActionCards;
